import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  Select,
  SelectItem,
} from "@heroui/react";
import { PencilSimple } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { DocumentTemplate, DocumentCategory } from "./types";

interface EditDocumentTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: DocumentTemplate | null;
  onSuccess: () => void;
}

const EditDocumentTemplateModal: React.FC<EditDocumentTemplateModalProps> = ({
  isOpen,
  onClose,
  template,
  onSuccess,
}) => {
  const { session } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    category: DocumentCategory;
    description: string;
  }>({
    name: "",
    category: DocumentCategory.GENERAL,
    description: "",
  });

  const categoryOptions = [
    { key: DocumentCategory.ACQUISITION, label: "Acquisitie" },
    { key: DocumentCategory.CONTRACT, label: "Contract" },
    { key: DocumentCategory.INSPECTION, label: "Inspectie" },
    { key: DocumentCategory.LEGAL, label: "Juridisch" },
    { key: DocumentCategory.RENOVATION, label: "Renovatie" },
    { key: DocumentCategory.GENERAL, label: "Algemeen" },
  ];

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        category: template.category,
        description: template.description || "",
      });
    }
  }, [template]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!template || !formData.name) {
      toast.error("Vul alle verplichte velden in");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/document-generation/templates/${template.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
        session,
      );

      if (response.ok) {
        toast.success("Document sjabloon succesvol bijgewerkt");
        onSuccess();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.message || "Fout bij het bijwerken van het sjabloon");
      }
    } catch (error) {
      console.error("Error updating template:", error);
      toast.error("Fout bij het bijwerken van het sjabloon");
    } finally {
      setIsLoading(false);
    }
  };

  if (!template) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader>
          <div className="flex items-center gap-2">
            <PencilSimple size={20} />
            Bewerk Document Sjabloon
          </div>
        </ModalHeader>
        <ModalBody className="space-y-4">
          <Input
            label="Naam"
            placeholder="bijv. Huurcontract Residentieel"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            isRequired
          />

          <Select
            label="Categorie"
            selectedKeys={formData.category ? [formData.category] : []}
            onSelectionChange={(keys) => {
              const key = Array.from(keys)[0] as DocumentCategory;
              handleInputChange("category", key);
            }}
            isRequired
          >
            {categoryOptions.map((option) => (
              <SelectItem key={option.key}>{option.label}</SelectItem>
            ))}
          </Select>

          <Textarea
            label="Beschrijving"
            placeholder="Beschrijf waarvoor dit sjabloon wordt gebruikt..."
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            rows={3}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>
            Annuleren
          </Button>
          <Button color="primary" onPress={handleSubmit} isLoading={isLoading}>
            Sjabloon Bijwerken
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditDocumentTemplateModal;
